import { useMutation } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { exists } from '~/utils'

import { type InputValues } from '../utils'

import { useBrandAccountId } from './useBrandAccountId'

const document = graphql(`
  mutation StrategyMemoEditPage_CreateStrategyMemo(
    $brandAccountId: String!
    $title: String!
  ) {
    createCustomerStrategyMemo(brandAccountId: $brandAccountId, title: $title) {
      id
    }
  }
`)

const createDocument = graphql(`
  mutation StrategyMemoEditPage_CreateStrategy($strategyMemoId: ID!) {
    createCustomerStrategy(customerStrategyMemoId: $strategyMemoId) {
      id
    }
  }
`)

const updateDocument = graphql(`
  mutation StrategyMemoEditPage_UpdateStrategy(
    $strategyId: ID!
    $how: String
    $segmentRoutes: [CustomerStrategyRouteInput!]
    $showArrow: Boolean
    $strategyType: CustomerStrategyType
    $what: String
    $who: String
  ) {
    updateCustomerStrategy(
      id: $strategyId
      how: $how
      segmentRoutes: $segmentRoutes
      showArrow: $showArrow
      strategyType: $strategyType
      what: $what
      who: $who
    ) {
      id
    }
  }
`)

export const useCreateStrategyMemoMutation = (
  options: MutationOptions<string, void> = {},
) => {
  const { t } = useTranslation()
  const graphQLRequest = useGraphQLRequest()
  const brandAccountId = useBrandAccountId()
  const { getValues } = useFormContext<InputValues>()

  return useMutation({
    mutationFn: async () => {
      const values = getValues()

      const result = await graphQLRequest({
        document,
        variables: { brandAccountId, title: values.title },
      })

      const strategyMemoId = result.createCustomerStrategyMemo?.id
      if (!exists(strategyMemoId)) {
        throw new Error(t('creation_failed_2'))
      }

      const strategies = values.strategies
      for (const strategy of strategies) {
        const createResult = await graphQLRequest({
          document: createDocument,
          variables: { strategyMemoId },
        })

        const strategyId = createResult.createCustomerStrategy?.id
        if (!exists(strategyId)) {
          throw new Error(t('creation_failed_3'))
        }

        const updateResult = await graphQLRequest({
          document: updateDocument,
          variables: { strategyId, ...R.omit(strategy, ['id']) },
        })
        const ok = exists(updateResult.updateCustomerStrategy?.id)
        if (!ok) {
          throw new Error(t('creation_failed_4'))
        }
      }

      return strategyMemoId
    },
    ...options,
  })
}
